import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const Emotion: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative py-40 md:py-52 px-6 overflow-hidden bg-cream">
      {/* BACKGROUND TEXTURE */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-soft-light bg-[url('/assets/textures/grain.png')]"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
        {/* ================= TEXT ================= */}
        <div className="lg:col-span-7">
          {/* EYEBROW */}
          <motion.span
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="
              block font-oswald text-sm uppercase tracking-[0.34em]
              text-charcoal mb-10
            "
          >
            {t("emotion.eyebrow", "Emotion")}
          </motion.span>

          {/* QUOTE */}
          <motion.blockquote
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="max-w-4xl"
          >
            <div className="flex items-start gap-6 mb-8">
              <motion.span
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.9 }}
                className="text-gold text-7xl md:text-8xl font-oswald leading-none"
                aria-hidden="true"
              >
                “
              </motion.span>

              <p
                className="
                  font-playfair font-light text-3xl md:text-4xl lg:text-[3rem]
                  leading-[1.3] text-stone-800
                "
              >
                {t(
                  "emotion.quote",
                  "Chaque propriété raconte une histoire — notre mission est de trouver celle qui vous ressemble."
                )}
              </p>
            </div>

            {/* AUTHOR */}
            <footer className="pl-7 border-l border-gold/40 mt-12">
              <span className="block font-oswald uppercase tracking-[0.2em] text-stone-500 text-sm">
                Dounia El Moutii
              </span>
              <span className="block text-[11px] text-stone-400 tracking-wider mt-1.5 uppercase font-sans">
                {t("emotion.role", "Founder & Real Estate Advisor")}
              </span>
            </footer>
          </motion.blockquote>
        </div>

        {/* ================= IMAGE ================= */}
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            className="
              relative w-[300px] h-[420px]
              sm:w-[340px] sm:h-[480px]
              lg:w-[380px] lg:h-[520px]
              rounded-2xl overflow-hidden
              shadow-[0_30px_60px_rgba(0,0,0,0.15)] z-10
              hover:shadow-[0_40px_80px_rgba(165,105,79,0.2)]
              hover:scale-[1.02] transition-all duration-[800ms] ease-out
            "
          >
            <img
              src="/img.jpeg"
              alt={t("emotion.imageAlt", "Portrait")}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />

            <div
              className="absolute inset-0 bg-gold/15 mix-blend-overlay"
              aria-hidden="true"
            />

            {/* CERTIFICATION BADGE */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
              className="
                absolute bottom-5 right-5
                w-20 h-20 sm:w-24 sm:h-24
                rounded-full
                bg-white/15 backdrop-blur-md
                border border-white/25
                shadow-[0_25px_60px_rgba(0,0,0,0.35)]
                flex items-center justify-center
              "
              title={t("emotion.certificationAlt", "Certification")}
            >
              <div className="absolute inset-0 rounded-full ring-1 ring-gold/30" />
              <img
                src="/certif.webp"
                alt={t("emotion.certificationAlt", "Certification")}
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain drop-shadow-md"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
          </motion.div>

          {/* SUBTLE SHAPE */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="
              absolute -right-10 top-10
              w-[240px] h-[240px]
              rounded-full
              border border-gold/30
              z-0
            "
            aria-hidden="true"
          />

          {/* ACCENT DOT */}
          <motion.div
            animate={{ opacity: [0.4, 0.85, 0.4], y: [0, -10, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            className="
              absolute right-12 top-20
              w-2 h-2
              bg-gold rounded-full
              shadow-[0_0_10px_#a5694f]
            "
            aria-hidden="true"
          />
        </div>
      </div>
    </section>
  );
};

export default Emotion;
